import { roleAtLeast, type ChannelView } from '@ai-sales/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { fetchChannels } from '../api/agents.api.js'
import {
  createChatAppChannel,
  fetchChannelWebhook,
  registerChannelWebhook,
} from '../api/messaging.api.js'
import { Badge } from '../components/badge.js'
import { Button } from '../components/button.js'
import { EmptyState } from '../components/data-table.js'
import { ErrorPanel } from '../components/error-panel.js'
import { Field, SelectField } from '../components/field.js'
import { PageHeader, Panel } from '../components/page.js'
import { useCurrentWorkspace } from '../workspace/workspace-context.js'

const EMPTY = {
  name: '',
  licenseId: '',
  messengerType: 'WhatsApp' as 'WhatsApp' | 'grWhatsApp' | 'caWhatsApp',
  phoneE164: '',
}

const MESSENGER_TITLES: Record<string, string> = {
  WhatsApp: 'WhatsApp — официальная линия (Cloud API)',
  grWhatsApp: 'WhatsApp — неофициальная линия',
  caWhatsApp: 'WhatsApp — линия ChatApp',
}

export function ChannelsPage() {
  const workspace = useCurrentWorkspace()
  const queryClient = useQueryClient()
  const canEdit = roleAtLeast(workspace.role, 'admin')

  const [form, setForm] = useState<typeof EMPTY | null>(null)

  const channels = useQuery({
    queryKey: ['channels', workspace.id],
    queryFn: () => fetchChannels(workspace.id),
  })

  const create = useMutation({
    mutationFn: (value: typeof EMPTY) =>
      createChatAppChannel(workspace.id, {
        name: value.name.trim(),
        licenseId: value.licenseId.trim(),
        messengerType: value.messengerType,
        phoneE164: value.phoneE164.trim(),
      }),
    onSuccess: async () => {
      setForm(null)
      await queryClient.invalidateQueries({ queryKey: ['channels', workspace.id] })
    },
  })

  function submit(event: FormEvent) {
    event.preventDefault()

    if (form && form.name.trim() !== '' && form.licenseId.trim() !== '') create.mutate(form)
  }

  const rows = channels.data ?? []

  return (
    <>
      <PageHeader
        title="Каналы"
        description="Линии, по которым приходят сообщения. Внутренний канал — место для проверок, линия ChatApp — настоящий WhatsApp."
        actions={
          canEdit && !form ? (
            <Button onClick={() => setForm(EMPTY)}>Подключить линию ChatApp</Button>
          ) : undefined
        }
      />

      <div className="flex flex-col gap-4">
        {channels.error && (
          <ErrorPanel error={channels.error} onRetry={() => void channels.refetch()} />
        )}

        {form && (
          <Panel
            title="Новая линия ChatApp"
            description="Все три значения переписываются из кабинета ChatApp — вывести их неоткуда."
          >
            <form onSubmit={submit} className="flex flex-col gap-3">
              <div className="grid gap-3 md:grid-cols-2">
                <Field
                  label="Название"
                  value={form.name}
                  required
                  hint="Как эта линия называется у вас: «WhatsApp Гватемала»"
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
                <Field
                  label="Номер лицензии"
                  value={form.licenseId}
                  required
                  hint="licenseId из кабинета ChatApp"
                  onChange={(event) => setForm({ ...form, licenseId: event.target.value })}
                />
              </div>

              <SelectField
                label="Тип линии"
                value={form.messengerType}
                hint="От него зависит предел длины сообщения: у официальной линии 4096 знаков, у неофициальной 20000"
                onChange={(event) =>
                  setForm({ ...form, messengerType: event.target.value as typeof form.messengerType })
                }
              >
                {Object.entries(MESSENGER_TITLES).map(([value, title]) => (
                  <option key={value} value={value}>
                    {title}
                  </option>
                ))}
              </SelectField>

              <Field
                label="Номер линии"
                value={form.phoneE164}
                required
                hint="В международном виде: +50255512233. Нужен, чтобы отличать линии глазами"
                onChange={(event) => setForm({ ...form, phoneE164: event.target.value })}
              />

              {create.error && <ErrorPanel error={create.error} />}

              <div className="flex flex-wrap gap-2">
                <Button type="submit" loading={create.isPending}>
                  Подключить
                </Button>
                <Button type="button" variant="ghost" onClick={() => setForm(null)}>
                  Отмена
                </Button>
              </div>
            </form>
          </Panel>
        )}

        {rows.length === 0 && !channels.isLoading && !form && (
          <EmptyState
            title="Каналов пока нет"
            description="Пока нет ни одной линии, сообщениям неоткуда приходить."
          />
        )}

        {rows.map((channel) => (
          <ChannelCard key={channel.id} channel={channel} canEdit={canEdit} />
        ))}
      </div>
    </>
  )
}

function ChannelCard({ channel, canEdit }: { channel: ChannelView; canEdit: boolean }) {
  const workspace = useCurrentWorkspace()
  const isChatApp = channel.provider === 'chatapp'

  return (
    <Panel
      title={channel.name}
      description={
        isChatApp
          ? `${MESSENGER_TITLES[channel.messengerType ?? ''] ?? channel.messengerType ?? ''} · лицензия ${channel.licenseId ?? '—'}`
          : 'Внутренний канал: сообщения никуда не уходят, путь — тот же'
      }
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={channel.status === 'active' ? 'ok' : 'neutral'}>
            {channel.status === 'active' ? 'работает' : 'отключён'}
          </Badge>
          {channel.isPaused && <Badge tone="warn">на паузе</Badge>}
          <Badge tone={channel.agentName ? 'ok' : 'warn'}>
            {channel.agentName ? `агент: ${channel.agentName}` : 'агент не назначен'}
          </Badge>
        </div>
      }
    >
      <dl className="grid gap-x-6 gap-y-2 text-sm md:grid-cols-2">
        <Row label="Номер" value={channel.phoneE164 ?? 'не указан'} />
        <Row label="Предел длины сообщения" value={`${channel.capabilities.maxTextLength} знаков`} />
        <Row label="Шаблоны" value={channel.capabilities.templates ? 'есть' : 'нет'} />
        <Row label="Окно 24 часа" value={channel.capabilities.serviceWindow ? 'есть' : 'нет'} />
      </dl>

      {isChatApp && canEdit && <Webhook workspaceId={workspace.id} channelId={channel.id} />}
    </Panel>
  )
}

/**
 * Адрес вебхука показывается по кнопке, а не сразу.
 *
 * В адресе секрет линии, и он не должен появляться на экране всякий
 * раз, когда кто-то открыл список каналов, — особенно если этот экран
 * показывают кому-то через плечо или на общем совещании.
 */
function Webhook({ workspaceId, channelId }: { workspaceId: string; channelId: string }) {
  const [shown, setShown] = useState(false)
  const [copied, setCopied] = useState(false)

  const webhook = useQuery({
    queryKey: ['channel-webhook', workspaceId, channelId],
    queryFn: () => fetchChannelWebhook(workspaceId, channelId),
    enabled: shown,
  })

  const register = useMutation({
    mutationFn: () => registerChannelWebhook(workspaceId, channelId),
  })

  if (!shown) {
    return (
      <div className="mt-4 border-t border-line pt-4">
        <Button variant="ghost" onClick={() => setShown(true)}>
          Показать адрес вебхука
        </Button>
      </div>
    )
  }

  return (
    <div className="mt-4 flex flex-col gap-3 border-t border-line pt-4">
      <p className="text-sm font-medium text-ink-soft">Адрес вебхука</p>

      {webhook.error && <ErrorPanel error={webhook.error} />}
      {webhook.isLoading && <p className="text-xs text-muted">Загружается…</p>}

      {webhook.data && !webhook.data.configured && (
        <p className="rounded-md border border-line bg-raised px-3 py-2 text-xs text-copper">
          Публичный адрес системы не задан, собрать ссылку не из чего. Пока система работает
          только на этом компьютере, ChatApp до неё не достучится — адрес появится после
          развёртывания на сервере.
        </p>
      )}

      {webhook.data?.configured && (
        <>
          <code className="block overflow-x-auto rounded-md border border-line bg-raised px-3 py-2 text-[13px] text-ink">
            {webhook.data.url}
          </code>
          <p className="text-xs text-muted">
            Этот адрес — пароль от линии: в нём секрет, по которому мы узнаём, что событие
            пришло от ChatApp. Не пересылайте его в переписке и не публикуйте.
          </p>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                void navigator.clipboard?.writeText(webhook.data.url)
                setCopied(true)
              }}
            >
              {copied ? 'Скопировано' : 'Скопировать'}
            </Button>
            <Button loading={register.isPending} onClick={() => register.mutate()}>
              Прописать в ChatApp
            </Button>
            <Button variant="ghost" onClick={() => setShown(false)}>
              Скрыть
            </Button>
          </div>

          {register.error != null && <ErrorPanel error={register.error} />}
          {register.isSuccess && (
            <p className="text-xs text-accent">
              Готово: ChatApp будет присылать сюда события «message».
            </p>
          )}
        </>
      )}
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="label-xs">{label}</dt>
      <dd className="text-ink">{value}</dd>
    </div>
  )
}
